import React, { useEffect } from "react";

import { Box } from "@chakra-ui/react";
import { useGetBusinessesStats } from "api/business-user-management/api-hooks";
import { DEFAULT_BUSINESSES_STATS } from "api/business-user-management/consts";
import {
  IBusinessListPayload,
  TBusinessOrderBy,
} from "api/business-user-management/types";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ROUTES } from "routes/constants";
import { replaceIdParamsInRouteWithValue } from "utils";

import { Tabs } from "ui/tabs";
import { ITabsData } from "ui/tabs/types";

import { BusinessListTable } from "../business-list-table";
import { BUSINESSES_LIST_TABLE_PAGINATION_PARAMS } from "../business-list-table/const";
import { IBusinessesContentProps } from "../header/types";

export enum BusinessesStatus {
  ALL = "all",
  ACTIVE = "active",
  INACTIVE = "inactive",
  DEACTIVATED = "deactivated",
}

export const BusinessesContent = (props: IBusinessesContentProps) => {
  const {
    handleGetBusinessesData,
    isLoadingBusinessesData,
    businesses,
    businessesData,
  } = props;
  const { data } = useGetBusinessesStats();
  const businessesStats = data?.data || DEFAULT_BUSINESSES_STATS;
  const { activeStatus } = useParams<{ activeStatus: BusinessesStatus }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const page = searchParams.get("page");
  const limit = searchParams.get("limit");
  const status = searchParams.get("status");
  const businessType = searchParams.get("businessType");
  const orderBy = searchParams.get("orderBy");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const search = searchParams.get("search");
  const dateRange = searchParams.get("dateRange");
  const filterRetryCount = searchParams.get("filterRetryCount");
  const tableRefreshCount = searchParams.get("tableRefreshCount");

  const handleTabSwitch = (newStatus: BusinessesStatus) => {
    let previousParamsWithoutOrderBy = location.search.replace("?", "");
    if (location.search.includes("orderBy")) {
      const params = new URLSearchParams(location.search);
      params.delete("orderBy");
      previousParamsWithoutOrderBy = params.toString();
    }

    const routeToNavigateTo = replaceIdParamsInRouteWithValue({
      route: ROUTES.BUSINESSES_PAGE,
      idParamsName: ":activeStatus",
      value: newStatus,
    });
    navigate(`${routeToNavigateTo}?${previousParamsWithoutOrderBy}`);
  };

  useEffect(() => {
    const payload: IBusinessListPayload = {
      page: Number(page || BUSINESSES_LIST_TABLE_PAGINATION_PARAMS.page),
      limit: Number(limit || BUSINESSES_LIST_TABLE_PAGINATION_PARAMS.limit),
    };

    if (dateRange) {
      payload["dateRange"] = dateRange;
    }

    if (startDate) {
      payload["startDate"] = startDate;
    }

    if (endDate) {
      payload["endDate"] = endDate;
    }

    if (orderBy) {
      payload["orderBy"] = orderBy as TBusinessOrderBy;
      payload["orderByField"] = "balance";
    }

    if (status) {
      const businessStatuses = status.split(",");
      if (!businessStatuses.includes("all")) {
        payload["status"] = businessStatuses.join(",");
      }
    }

    if (businessType) {
      const businessTypes = businessType.split(",");
      const filteredBusinessTypes = businessTypes.filter(
        (businessType) => businessType !== "all"
      );
      payload["companyType"] = filteredBusinessTypes.join(",");
    }

    if (search) {
      payload["search"] = search;
      payload["searchBy"] =
        "email,firstName,lastName,companyData.companyName,firstName_lastName";
    }

    switch (activeStatus) {
      case BusinessesStatus.ACTIVE:
        void handleGetBusinessesData({
          ...payload,
          isActive: true,
        });
        break;
      case BusinessesStatus.INACTIVE:
        void handleGetBusinessesData({
          ...payload,
          isActive: false,
        });
        break;
      case BusinessesStatus.DEACTIVATED:
        void handleGetBusinessesData({
          ...payload,
          deactivated: true,
          disabled: true,
        });
        break;
      default:
        void handleGetBusinessesData({
          ...payload,
        });
        break;
    }
  }, [
    activeStatus,
    page,
    limit,
    status,
    startDate,
    endDate,
    orderBy,
    search,
    dateRange,
    businessType,
    filterRetryCount,
    tableRefreshCount,
  ]);

  const tabHeadersAsLinks: ITabsData[] = [
    {
      value: BusinessesStatus.ALL,
      content: (
        <Box onClick={() => handleTabSwitch(BusinessesStatus.ALL)}>
          All ({businessesStats.total})
        </Box>
      ),
    },
    {
      value: BusinessesStatus.ACTIVE,
      content: (
        <Box onClick={() => handleTabSwitch(BusinessesStatus.ACTIVE)}>
          Active ({businessesStats.active})
        </Box>
      ),
    },
    {
      value: BusinessesStatus.INACTIVE,
      content: (
        <Box onClick={() => handleTabSwitch(BusinessesStatus.INACTIVE)}>
          Inactive ({businessesStats.inactive})
        </Box>
      ),
    },
    {
      value: BusinessesStatus.DEACTIVATED,
      content: (
        <Box onClick={() => handleTabSwitch(BusinessesStatus.DEACTIVATED)}>
          Deactivated ({businessesStats.deactivated})
        </Box>
      ),
    },
  ];

  const tabsContentAsLinks: ITabsData[] = [
    {
      value: BusinessesStatus.ALL,
      content: <></>,
    },
    {
      value: BusinessesStatus.ACTIVE,
      content: <></>,
    },
    {
      value: BusinessesStatus.INACTIVE,
      content: <></>,
    },
    {
      value: BusinessesStatus.DEACTIVATED,
      content: <></>,
    },
  ];

  return (
    <Box>
      <Tabs
        headers={tabHeadersAsLinks}
        contentList={tabsContentAsLinks}
        renderTabNavAsLink
        tabsListHeaderCss={{ display: "flex" }}
        tabsContentCss={{ padding: 0, marginBottom: "8px" }}
        defaultTabValue={activeStatus}
      />
      <BusinessListTable
        businesses={businesses}
        businessesData={businessesData}
        isLoadingBusinessesData={isLoadingBusinessesData}
        handleGetBusinessesData={handleGetBusinessesData}
      />
    </Box>
  );
};
